import { Dialog, Fade } from "@mui/material";
import Box from "@mui/system/Box";
import { useFormik } from "formik";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { snackAdded } from "src/slices/system";
import { languages, locations, usStates } from "src/slices/brief/helpers";
import { getProjects, projects } from "src/slices/projects";
// import { useDispatch } from "src/store";
import { checkBriefStatus, createBrief } from "src/slices/brief/thunks";
import { useDispatch } from "src/store";

import * as yup from "yup";
import ChooseKeyword from "./chooseKeyword";
import Error from "./error";
import Loader from "./loader";
import Success from "./success";

interface OptimizeContentData {
  keyword: string;
  articleLink: string;
  location: (typeof locations)[0];
  state: (typeof usStates)[0] | null;
  language: string;
  project: any;
  stats: boolean;
  insights: string;
}

interface Props {
  open: boolean;
  limit: boolean;
  isRedirect?: boolean;
  seoMissionsKeyword?: string;
  seoMissionsKeywordId?: string;
  isSeoMissionsBrief?: boolean;
  onClose?: (briefId: string) => void;
  onCreated: () => void;
  onOptimizeContent?: (data: OptimizeContentData) => void;
}

export const CreateBriefDialog = ({
  open,
  limit,
  isRedirect,
  seoMissionsKeyword,
  seoMissionsKeywordId,
  onClose,
  onCreated,
  onOptimizeContent,
}: Props) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(0);

  let location = {
    location_name: "United States",
    country_iso_code: "US",
  };

  try {
    location = JSON.parse(localStorage.getItem("brief-location")) || location;
  } catch (error) {}

  useEffect(() => {
    dispatch(getProjects());
  }, []);
  const router = useRouter();

  const Projects = useSelector(projects);

  const language = localStorage.getItem("brief-language") ?? "English";
  let project = null;
  let state = null;
  try {
    project = JSON.parse(localStorage.getItem("brief-project")) ?? Projects[0];
  } catch (error) {}

  try {
    const savedState = localStorage.getItem("brief-state");

    if (savedState) {
      state = JSON.parse(savedState);

      // Validate state is only used when location is United States
      if (state && location?.location_name !== "United States") {
        state = null;
        localStorage.removeItem("brief-state");
      } else {
      }
    } else {
    }
  } catch (error) {
    console.error("❌ Error loading state:", error);
  }

  const [briefId, setBriefId] = useState("");

  const formik = useFormik<{
    keyword: string;
    location: (typeof locations)[0];
    state: (typeof usStates)[0] | null;
    language: string;
    project: {
      projectName: string;
      projectId: string;
      projectDomain: string;
      isGSCIntegrated: boolean;
      companyDetails: any;
      GSCsiteUrl: string;
    };
    generateArticle: boolean;
    interlinking: boolean;
    stats: boolean;
    thoughtLeadership: boolean;
    insights: string;
    generateAutomatedInsights: boolean;
    companyDetails: boolean;
    humanize: boolean;
    aiResearchAgent: boolean;
    aiEvalRefineAgent: boolean;
  }>({
    initialValues: {
      keyword: "",
      location,
      state,
      language,
      project,

      generateArticle: false,
      interlinking: true,
      stats: true,
      insights: "",
      generateAutomatedInsights: false,
      thoughtLeadership: true,
      companyDetails: true,
      humanize: false,
      aiResearchAgent: true,
      aiEvalRefineAgent: true,
    },

    onSubmit: async (values) => {
      if (limit) {
        setCurrentStep(3);
        return;
      }
      setCurrentStep(1);
      const languageCode = languages.find(
        (value) => value.languageName === formik.values.language
      ).languageCode;

      // Use state location name if US state is selected, otherwise use country
      const locationName =
        formik.values.state?.location_name ||
        formik.values.location.location_name;

      // projects commented out
      const seoMissionBriefId = seoMissionsKeywordId ?? "";
      const projectId = formik.values.project.projectId;
      const projectName = formik.values.project.projectName;
      const projectDomain = formik.values.project.projectDomain;
      const GSCsiteUrl = formik.values.project.GSCsiteUrl;
      const briefPromise = dispatch(
        createBrief({
          languageCode,
          keyword: formik.values.keyword,
          locationName: locationName,
          languageName: formik.values.language,
          projectId: projectId,
          projectName: projectName,
          seoMissionsBriefId: seoMissionBriefId,
          isOnboardingBrief: false,
          generateArticle: values.generateArticle,
          GSCsiteUrl: GSCsiteUrl ?? "",
          companyDetails: formik.values.companyDetails,
          stats: false,
          insights: formik.values.insights,
          generateAutomatedInsights: formik.values.generateAutomatedInsights,
          interlinking: formik.values.interlinking,
          humanize: formik.values.humanize,
          thoughtLeadership: false,
          aiResearchAgent: formik.values.aiResearchAgent,
          aiEvalRefineAgent: formik.values.aiEvalRefineAgent,
        })
      );

      // Immediately proceed to success step
      formik.setFieldValue("keyword", "");
      setCurrentStep(2);

      // Handle brief creation result in background
      briefPromise
        .then((brief) => {
          if ((brief as any).payload?.id) {
            setBriefId((brief as any).payload.id);
            // Optionally show success notification
            console.log("Brief created successfully:", (brief as any).payload.id);
          } else {
            // Brief creation failed but don't block UI
            console.error("Brief creation failed - no ID returned", brief);
            dispatch(snackAdded({
              message: "Failed to create brief. Please try again.",
              type: "error"
            }));
            setCurrentStep(3); // Show error state
          }
        })
        .catch((error) => {
          // Handle API errors without blocking UI
          console.error("Brief creation error:", error);
          dispatch(snackAdded({
            message: "Failed to create brief. Please check your connection and try again.",
            type: "error"
          }));
        });
    },
    validationSchema: yup.object({
      keyword: yup.string().max(255).required("Focus keyword is required"),
      location: yup.object().required("Location is required").nullable(),
      project: yup.object().required("Project is required").nullable(),
    }),
  });

  const handleOnClose = () => {
    formik.setFieldValue("keyword", "");
    formik.setFieldValue("insights", "");
    onClose?.(briefId);
    setCurrentStep(0);
  };

  const handleCreateAnotherBrief = () => {
    formik.setFieldValue("keyword", "");
    formik.setFieldValue("insights", "");
    // formik.setFieldValue("i"Projects[0]);
    setCurrentStep(0);
    onCreated();
    //chcek brief status
  };

  const checkBriefStatusRecursive = async (briefId: string) => {
    try {
      let res = await dispatch(
        checkBriefStatus({
          id: briefId,
        })
      ).unwrap();
      //update stepRemains in the brief
      if (res?.stepRemains !== 0 && !res?.retry) {
        setTimeout(() => {
          checkBriefStatusRecursive(res?.id);
        }, 25000);
      } else if (
        res?.stepRemains === 0 &&
        res?.generateArticle === true &&
        res?.articleStatus === undefined &&
        !res?.retry
      ) {
        setTimeout(() => {
          checkBriefStatusRecursive(res?.id);
        }, 10000);
      }
    } catch (error) {
      console.error("Error checking brief status:", error);
    }
  };

  useEffect(() => {
    formik.setFieldValue("keyword", seoMissionsKeyword);
  }, [seoMissionsKeyword]);

  // Validate state when location changes
  useEffect(() => {
    if (
      formik.values.location?.location_name !== "United States" &&
      formik.values.state
    ) {
      formik.setFieldValue("state", null);
      localStorage.removeItem("brief-state");
    }
  }, [formik.values.location]);

  const componentsMap = {
    0: <ChooseKeyword formik={formik} onOptimizeContent={onOptimizeContent} />,
    1: <Loader />,
    //add a loading component here for step 2
    2: (
      <Success
        isRedirect={isRedirect}
        onClose={handleOnClose}
        onCreated={handleCreateAnotherBrief}
      />
    ),
    3: <Error onClose={handleOnClose} />,
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleOnClose}
      TransitionComponent={Fade}
      TransitionProps={{
        timeout: 300,
      }}
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" }, mt: 5.5 }}
    >
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          height: "8px",
          borderRadius: "8px 8px 0px 0px",
          background:
            "linear-gradient(60.66deg, #FFD875 -3.87%, #DA00C8 26.08%, #6A00FF 56.49%)",
        }}
      />

      <form onSubmit={formik.handleSubmit}>{componentsMap[currentStep]}</form>
    </Dialog>
  );
};
